import React, { useMemo } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

// 🎯 Custom Tooltip
const CustomTooltip = ({ active, label, payload, orders = [], navigate }) => {
  if (!active || !payload || !label) return null;

  const dayLabel = moment(label, "ddd D").format("YYYY-MM-DD");

  const dayOrders = orders.filter(
    (o) => moment(o.createdAt).format("YYYY-MM-DD") === dayLabel,
  );

  return (
    <div className="bg-white border border-gray-100 rounded-xl p-3 shadow-lg text-xs w-56 max-h-64 overflow-y-auto">
      <p className="text-navy font-semibold mb-2">{label}</p>

      {dayOrders.length === 0 ? (
        <p className="text-gray-400 italic">No orders</p>
      ) : (
        dayOrders.map((o) => {
          const time = moment(o.createdAt).format("hh:mm A");

          return (
            <div
              key={o._id}
              onClick={() => navigate(`/order/preview/${o._id}`)}
              className="cursor-pointer mb-1 hover:text-navy transition"
            >
              <span className="font-medium">
                {o.user?.displayName || "Customer"}
              </span>{" "}
              <span className="text-gray-400">• {time}</span>
            </div>
          );
        })
      )}
    </div>
  );
};

const UpcomingOrders = ({ orders = [] }) => {
  const navigate = useNavigate();
  const today = moment().startOf("day");

  // 📊 Chart Data
  const data = useMemo(() => {
    const days = [];

    for (let i = 0; i < 7; i++) {
      const day = today.clone().add(i, "days");

      days.push({
        name: day.format("ddd D"),
        date: day.format("YYYY-MM-DD"),
        Confirmed: 0,
        Cancelled: 0,
      });
    }

    orders.forEach((order) => {
      const dayStr = moment(order.createdAt).format("YYYY-MM-DD");
      const status = order.orderStatus?.toLowerCase();

      const dayData = days.find((d) => d.date === dayStr);

      if (dayData) {
        if (status === "cancelled") dayData.Cancelled += 1;
        else dayData.Confirmed += 1;
      }
    });

    return days;
  }, [orders]);

  const totalConfirmed = data.reduce((sum, d) => sum + d.Confirmed, 0);
  const totalCancelled = data.reduce((sum, d) => sum + d.Cancelled, 0);
  const total = totalConfirmed + totalCancelled;

  return (
    <div className="p-6 rounded-2xl border border-navy shadow-sm hover:shadow-md transition min-h-[500px]">
      {/* HEADER */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <p className="text-xs uppercase tracking-widest text-gray-400">
            Upcoming Orders
          </p>

          <div className="flex items-end gap-3 mt-2">
            <h2 className="text-4xl font-bold text-gray-900">{total}</h2>
            <span className="text-sm text-gray-400 pb-1">next 7 days</span>
          </div>

          <p className="text-sm text-gray-400 mt-1">
            {totalConfirmed} confirmed • {totalCancelled} cancelled
          </p>
        </div>

        <div className="px-3 py-1 text-xs rounded-full bg-green-50 text-green-600 font-medium">
          Live
        </div>
      </div>

      {/* CHART */}
      <div className="h-[220px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data}>
            <XAxis
              dataKey="name"
              tick={{ fontSize: 11, fill: "#132861" }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              allowDecimals={false}
              tick={{ fontSize: 11, fill: "#132861" }}
              axisLine={false}
              tickLine={false}
            />

            <Tooltip
              content={<CustomTooltip orders={orders} navigate={navigate} />}
            />

            <Bar dataKey="Confirmed" fill="#132861" radius={[6, 6, 0, 0]} />
            <Bar dataKey="Cancelled" fill="#ef4444" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* LEGEND */}
      <div className="flex gap-6 mt-4 text-xs text-gray-500">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-yellow-400" />
          Confirmed
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-red-500" />
          Cancelled
        </div>
      </div>

      {/* LIST */}
      <div className="mt-6 border-t pt-4">
        <p className="text-sm font-semibold text-gray-700 mb-3">
          Upcoming (next 7 days)
        </p>

        <div className="max-h-40 overflow-y-auto space-y-2 pr-2">
          {orders
            .filter((o) => {
              const date = moment(o.createdAt);
              return (
                date.isSameOrAfter(today) &&
                date.isBefore(today.clone().add(7, "days"))
              );
            })
            .sort((a, b) => moment(a.createdAt) - moment(b.createdAt))
            .map((o, index) => {
              const date = moment(o.createdAt).format("ddd, MMM D");
              const time = moment(o.createdAt).format("hh:mm A");

              return (
                <div
                  key={o._id}
                  onClick={() => navigate(`/order/preview/${o._id}`)}
                  className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 cursor-pointer transition"
                >
                  <span className="text-xs text-gray-400 w-5">
                    {index + 1}.
                  </span>

                  <span className="flex-1 font-medium text-gray-900">
                    {o.user?.displayName || "Customer"}
                  </span>

                  <span className="text-xs text-gray-400">
                    {date} • {time}
                  </span>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};

export default UpcomingOrders;
